import React from 'react';
import type { MfaFactorTier } from '@usacsmideo/types';
import { usePasskeyEnrollment } from '../../hooks/usePasskeyEnrollment';
import { usePasskeyLogin } from '../../hooks/usePasskeyLogin';
import { Button } from '../buttons/Button';
import { PasskeyEnrollmentSection } from './PasskeyEnrollmentSection';
import { SmsEnrollmentSection } from './SmsEnrollmentSection';
import { emitFactorAttempt, emitGateBlocked, emitGateShown } from '../../utils/mfa/factorChoiceTelemetry';
import { useEmailVerificationGate } from '../../hooks/useEmailVerificationGate';

/**
 * The PHI gate's FACTOR CHOICE — CU-86ajzr9fp (sub-task 4 of CU-86ajzr89h).
 *
 * Replaces the gate's single SMS path. `MfaRouteGate.tsx` previously carried the
 * comment *"When passkeys ship, this becomes a factor CHOICE and `onCancel`
 * returns to it."* — this is that.
 *
 * ── ⚠️ THE CHOICE IS THREE-WAY, NOT TWO ───────────────────────────────────
 *
 *   Tier 0a  USE the passkey you already have   ← the common case, offered FIRST
 *   Tier 0b  Set up a passkey                   ← only when they have none
 *   Tier 1   Use a texted code (SMS)            ← ALWAYS available, never gated
 *   Tier 2   grace extension                    ← NEVER offered; routes to support
 *
 * ⚠️ WHY 0a EXISTS AT ALL — CU-86ajw3hv9, measured on device by the mobile app.
 * A passkey does NOT mint `aal2` on a PASSWORD login; only `passkeyVerify` does.
 * So a passkey-enrolled patient who signs in with their password arrives HERE at
 * AAL1 **holding a working credential**, and the way out is to USE it, not to
 * make a second one.
 *
 * ⚠️ REGISTERING ANOTHER ONE IS NOT WHAT THEY NEED. Telling them to "set up a
 * passkey" reads as *"yours didn't count"* and steers them to SMS — the
 * restricted authenticator we most want to avoid, and the one a patient without
 * a usable phone cannot use at all.
 *
 * ⚠️ TIER ORDER IS LOAD-BEARING, NOT COSMETIC. A passkey needs NO PHONE NUMBER.
 * That is the entire reason a patient without a usable phone is not stranded
 * (NO-STRAND-ANYFACTOR). Reordering these silently strands that population.
 *
 * ── ⚠️ THE LIST READ IS WHAT ANSWERS THE QUESTION ─────────────────────────
 * `refreshCredentials()` fires ON MOUNT (see below), so in the ordinary case the
 * gate KNOWS whether this patient holds a portal credential before offering
 * anything. An empty list yields the honest "Set up a passkey".
 *
 * ⚠️ THIS PARAGRAPH USED TO DESCRIBE AN ARCHITECTURE THAT DID NOT EXIST. The
 * mount read was missing entirely (adversarial round 1, F1): the only call site
 * was `backToChoice`, reachable only after visiting a sub-view. So `credentials`
 * stayed `null` forever, `likelyHasPasskey` was permanently true, and **"Set up
 * a passkey" was unreachable in production** while every state was reachable
 * under the component test's mock.
 *
 * `credentials === null` still means UNKNOWN — the read is in flight or it
 * FAILED — and we still offer "use" there, because the costs are ASYMMETRIC:
 *   • a wrong "use"    → one wasted tap, with an offered route to enrollment.
 *   • a wrong "set up" → REPRODUCES CU-86ajw3hv9 exactly.
 *
 * ⚠️ WHAT DOES *NOT* HOLD ON WEB: the ceremony cannot tell us the store is
 * empty. See the escape-hatch note further down — `no-credentials` is
 * unreachable from this surface, and an earlier version of this header claimed
 * otherwise while calling it verified.
 *
 * ── ⚠️ WHY `listMyPasskeys()` AND NOT `getPasskeyStatus` ───────────────────
 * The mobile gate reads `getPasskeyStatus`. ⚠️ DO NOT PORT THAT CALL. Its
 * response is GLOBAL ACROSS SURFACES and cannot be scoped — `PasskeyStatusPayload`
 * has no `surface` field and deliberately never gained one (`passkeyClient.ts`
 * header). A patient holding only a MOBILE passkey would read as `enrolled: true`,
 * we would offer "use your passkey", and the ceremony would find nothing on this
 * RP — a dead end at the control standing between them and their records.
 *
 * The surface-filtered `listMyPasskeys()` IS the accurate per-surface count, which
 * is why CU-86ajz1bhx closed that gap with the list rather than a field on status.
 * Mobile reached for status because MOBILE HAS NO PER-CREDENTIAL LIST; the portal
 * has the better instrument. The semantics port, the call does not.
 *
 * ⚠️ That reasoning was sound and the call was simply never made — the comment
 * argued for an instrument the component did not use. Both halves are now true:
 * the argument, and the mount read that acts on it.
 *
 * ── ⚠️ NEVER WEDGED ───────────────────────────────────────────────────────
 * Sign-out is always reachable, from every sub-view. A patient must always be
 * able to leave a screen they cannot satisfy. Tier 2 is never offered here: it is
 * an admin-granted, expiring `mfa.exception`, so a patient who can do neither
 * tier is routed to support rather than shown a control they cannot action.
 */

/**
 * Which sub-view the gate is showing. `choose` is the hub everything returns to.
 *
 * ⚠️ `unverified` IS NOT IN THIS UNION, DELIBERATELY — CU-86akjgbyw. It is not a
 * view the patient NAVIGATES to; it is a precondition that preempts the whole
 * screen. Putting it here would make it reachable by `setView`, and therefore
 * escapable by any existing `setView('choose')` — of which there are five. It
 * is rendered from the verification state instead, ahead of every branch below.
 */
type GateView = 'choose' | 'enroll-passkey' | 'enroll-sms';

export interface MfaGateFactorChoiceProps {
  /** Re-decide the gate once a factor is satisfied. */
  onComplete: () => void;
  /** ⚠️ Always rendered. The gate must never wedge a patient. */
  onSignOut?: () => void;
}

export function MfaGateFactorChoice({ onComplete, onSignOut }: MfaGateFactorChoiceProps) {
  const [view, setView] = React.useState<GateView>('choose');

  const { supported, credentials, credentialsLoading, refreshCredentials } = usePasskeyEnrollment();

  /**
   * ⚠️ THE EMAIL-VERIFICATION PRECONDITION — CU-86akjgbyw (part 3 of CU-86akjgbpk).
   *
   * Verifying an email bumps `tokensValidAfterTime`, and the H4 staleness check
   * (`passkeyVerify.ts:321-327`) then rejects AND DELETES every passkey enrolled
   * beforehand — fire-and-forget, no audit row, no notification. Reproduced
   * 2026-09-15.
   *
   * ⚠️ THE ASYMMETRY IS WHAT MAKES THIS THE LIKELY PATH, NOT AN EDGE CASE.
   * Identity Platform refuses SMS until the email is verified, so an unverified
   * patient can complete ONLY a passkey — and this gate offers passkey FIRST, as
   * Tier 0. Enrol → later try SMS → told to verify → verify → passkey destroyed.
   *
   * Hiding BOTH tiers until verified makes that order UNREACHABLE rather than
   * merely discouraged. That is the whole point of the task: a warning alone
   * would relocate the silent destruction, not remove it.
   */
  const emailVerification = useEmailVerificationGate();

  /**
   * ⚠️ THE READ THAT MAKES EVERY DECISION BELOW REAL — adversarial round 1, F1.
   *
   * Without this the list is NEVER fetched on this surface: the only other call
   * site is `backToChoice`, reachable only after a patient has already visited a
   * sub-view. `credentials` stayed `null` forever, so `likelyHasPasskey` was
   * permanently true, **"Set up a passkey" was unreachable in production**, and
   * the whole `statusSettled` apparatus was inert — `credentialsLoading` is
   * false on the first render, so it flipped settled immediately.
   *
   * ⚠️ THE MOCKED SUITE COULD NOT SEE IT. The component test supplies
   * `credentials` as a pre-set value, simulating a fetch production never
   * performed — so every state was reachable in tests and only one was reachable
   * in the app. `MfaGateFactorChoice.mount.test.tsx` now drives the REAL hook
   * with only the network layer stubbed, because that is the only shape that
   * catches this.
   *
   * ⚠️ `refreshCredentials` is deliberately NOT in the dependency array — it is
   * recreated whenever `deps` changes identity, and including it would re-run
   * this on every render. That is the "unstable callback identity" family, which
   * has now cost this workstream four separate defects. Same discipline as
   * `PasskeyEnrollmentSection`, which this mirrors.
   */
  React.useEffect(() => {
    void refreshCredentials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * ⚠️ `statusSettled` IS DERIVED HERE, NOT READ FROM THE HOOK.
   *
   * `usePasskeyEnrollment` has no such output, and its `credentials === null`
   * CANNOT distinguish "in flight" from "failed": on a failed list it never
   * calls `setCredentials`, so `credentials` stays null while
   * `credentialsLoading` returns to false.
   *
   * ⚠️ THAT CONFLATION IS THE BUG MOBILE PAID FOR (CU-86ajx3xg7, measured on
   * device 2026-08-06). Gating the control on `credentials === null` left the
   * button disabled FOREVER whenever the read could not succeed — and that is
   * not an edge case: it hits every patient who reaches this gate OFFLINE,
   * because the read is a network call.
   *
   * So the control is gated on SETTLED — resolved OR failed — never on the
   * value. A failed read still leaves `credentials === null`, which keeps the
   * optimistic "use" bias below, which is the correct guess.
   *
   * Derived at the call site rather than widening a shipped hook that has two
   * other consumers.
   */
  const [statusSettled, setStatusSettled] = React.useState(false);
  const readStartedRef = React.useRef(false);
  React.useEffect(() => {
    // ⚠️ MEASURED, NOT REASONED ABOUT — and the first version was WRONG.
    //
    // It read `if (credentials !== null || !credentialsLoading)`, which is TRUE
    // on the very first render: `credentialsLoading` starts false and the hook
    // only sets it true inside its own effect, which runs AFTER this one. So the
    // control was released before the read had even begun, and the "Checking…"
    // state was unreachable. The mocked component suite could not see this —
    // it supplies `credentialsLoading` as a pre-set value, so the real ordering
    // never occurred there.
    //
    // Caught by `MfaGateFactorChoice.mount.test.tsx`, which drives the real hook
    // against a deferred list call. Corrected to: settled once a read has
    // actually STARTED and then finished — or once one produced a value.
    if (credentialsLoading) readStartedRef.current = true;
    if (credentials !== null) setStatusSettled(true);
    else if (readStartedRef.current && !credentialsLoading) setStatusSettled(true);
  }, [credentials, credentialsLoading]);

  /**
   * ⚠️ OPTIMISTIC BY DESIGN — `null` (unknown) counts as "probably has one".
   * See the header: the costs are asymmetric and the wrong "use" is recoverable.
   */
  const likelyHasPasskey = credentials === null || credentials.length > 0;

  /**
   * ⚠️ THE FUNNEL'S DENOMINATOR — CU-86ak54cyz.
   *
   * Emitted from the RENDER's own view of what it offered, never from config:
   * Tier 0 disappears entirely without WebAuthn support, and the choice between
   * `passkey-use` and `passkey-enroll` comes from a credential list that may
   * still be in flight. Only this component knows what the patient could see.
   *
   * ⚠️ `statusSettled` RIDES ALONG because an unsettled render's offer is a
   * GUESS — `likelyHasPasskey` is optimistic on `null`, so the gate can offer
   * "use your passkey" to someone holding none. Without that flag the funnel
   * would score those as passkey failures rather than as a known consequence of
   * an unresolved read.
   *
   * Deduplicated on the offer set inside `emitGateShown`, so re-renders and the
   * twin `useMfaRouting` call sites cannot inflate the denominator.
   */
  React.useEffect(() => {
    /**
     * ⚠️ A BLOCKED GATE EMITS `gate-blocked` AND NOTHING ELSE — CU-86akjgbyw.
     *
     * Not `emitGateShown([], …)`: an empty offer set reads as "gate shown,
     * nothing taken", which would score every blocked patient as one who was
     * offered factors and refused them all — silently depressing the adoption
     * DENOMINATOR this effect exists to establish.
     *
     * ⚠️ AND NOT BOTH. Emitting a `gate-shown` alongside would double-count the
     * denominator for exactly the population that saw no offer.
     *
     * ⚠️ `checking` EMITS NOTHING. The first render has not yet decided, and an
     * event per intermediate state would make the funnel count mounts rather
     * than presentations.
     */
    if (emailVerification.state === 'checking') return;

    if (emailVerification.blocksEnrollment) {
      emitGateBlocked('unverified-email', likelyHasPasskey);
      return;
    }

    const offered: MfaFactorTier[] = [];
    if (supported) offered.push(likelyHasPasskey ? 'passkey-use' : 'passkey-enroll');
    // ⚠️ SMS is ALWAYS offered and never gated behind passkey support — it sits
    // outside the `supported` check in the render, and must here too.
    offered.push('sms');
    emitGateShown(offered, statusSettled);
  }, [
    supported,
    likelyHasPasskey,
    statusSettled,
    emailVerification.state,
    emailVerification.blocksEnrollment,
  ]);

  const [useError, setUseError] = React.useState<string | null>(null);

  /** Set by the success callback; read after the ceremony settles. See `handleUse`. */
  const succeededRef = React.useRef(false);

  const handleUsed = React.useCallback(() => {
    // Marks the ceremony as having actually established a session, so the
    // fall-through detector below can tell "did nothing" from "worked".
    succeededRef.current = true;
    setCeremonyFellThrough(false);
    // ⚠️ THE ADOPTION SUCCESS SIGNAL. A patient who already held a credential
    // and simply presented it is the case that decides whether passkey-only is
    // viable — distinct from `passkey-enroll`, which is an adoption COST.
    emitFactorAttempt('passkey-use', 'succeeded');
    onComplete();
  }, [onComplete]);

  /**
   * ⚠️ MEMOISED — AN INLINE ARROW HERE DROVE AN UNBOUNDED LOOP.
   *
   * This was `onComplete={() => { … }}` inline. Every render minted a new
   * function identity, and `SmsEnrollmentSection`'s completion effect had
   * `onComplete` in its dependency array — so completing enrolment re-rendered
   * this component, which changed the prop, which re-fired the effect.
   * MEASURED: **20,379 requests** to `securetoken.googleapis.com` from one
   * enrolment (staging, 2026-09-02), because each turn forced a token refresh.
   *
   * ⚠️ `SmsEnrollmentSection` was ALSO latched so it cannot re-fire on a prop
   * identity change. Either fix alone stops the loop; both are kept because
   * this one is a property of this call site, and the latch is a property of
   * the component — the latter protects call sites nobody has written yet.
   */
  const handleSmsComplete = React.useCallback(() => {
    emitFactorAttempt('sms', 'succeeded');
    onComplete();
  }, [onComplete]);

  // ⚠️ `armConditional` is deliberately NOT called here. Conditional mediation
  // needs an `autocomplete="… webauthn"` input in the DOM, and this surface has
  // no email field — arming without one throws and is swallowed silently by
  // design (sub-task 3, adversarial round 2 F1). The gate uses the MODAL
  // ceremony only, which is the right shape: we already know who the patient is.
  const { busy: loginBusy, error: loginError, signInWithPasskey } = usePasskeyLogin(handleUsed);

  /**
   * ⚠️ THE ESCAPE HATCH — adversarial round 1, F2.
   *
   * ⚠️ ON WEB, AN EMPTY CREDENTIAL STORE AND A DISMISSAL ARE THE SAME EVENT.
   * The spec collapses both into `NotAllowedError`, which `webPasskeyProvider`
   * maps to `cancelled`. `no-credentials` exists in the union but is reachable
   * ONLY via conditional mediation (`usePasskeyLogin.ts:504`), which this
   * surface deliberately does not use — so it can never arrive here.
   *
   * ⚠️ THIS FILE PREVIOUSLY CLAIMED THE OPPOSITE, calling the fallthrough
   * "✅ VERIFIED, not assumed". It was verified only that `no-credentials`
   * EXISTS as an error kind, never that the gate's ceremony path can PRODUCE
   * one. The measured consequence: a patient with no passkey pressed the
   * offered button and nothing happened, leaving SMS as the only exit — the
   * exact outcome this design exists to prevent, and unusable for a patient
   * without a phone.
   *
   * ⚠️ DO NOT TRY TO TELL THE TWO APART. `passkeyFlows.ts` records that mobile
   * already paid for this conflation on iOS — "that difference is exactly what
   * left patients stuck." The web's advantage is the LIST READ, not a better
   * error signal, which is why the mount read above is the primary fix: an
   * empty list answers the question BEFORE any ceremony runs.
   *
   * This is the second line of defence, for the case the list could not answer
   * (a failed read, or a stale list). It is OFFERED, never taken automatically:
   * a patient who genuinely dismissed the sheet must not be yanked into a
   * ceremony they just declined.
   */
  const [ceremonyFellThrough, setCeremonyFellThrough] = React.useState(false);

  React.useEffect(() => {
    if (!loginError) return;
    setCeremonyFellThrough(false);
    setUseError(
      loginError.kind === 'network'
        ? "We couldn't reach the server. Please check your connection and try again."
        : loginError.kind === 'unsupported'
          ? "This browser can't use passkeys. You can use a texted code instead."
          : "That didn't work. Please try again, or use a texted code instead.",
    );
    /**
     * ⚠️ THE IRRECOVERABLE SIGNAL — a passkey attempt that FAILED.
     *
     * Afterwards this patient is indistinguishable from one who simply chose
     * SMS: both end up holding an SMS factor. Only this moment records that they
     * TRIED the passkey first and it did not work, which is the difference
     * between "patients prefer SMS" and "passkeys are failing and SMS is
     * catching them" — opposite conclusions for the retirement decision.
     *
     * ⚠️ MAPPED TO THE CLOSED UNION, never the raw error. `loginError.kind` is
     * already a classified union; anything unrecognised becomes `unclassified`,
     * which is an honest answer rather than a smuggled string.
     */
    emitFactorAttempt(
      'passkey-use',
      'failed',
      loginError.kind === 'network'
        ? 'network'
        : loginError.kind === 'unsupported'
          ? 'unsupported'
          : 'rejected',
    );
  }, [loginError]);

  /**
   * Run the use-ceremony and notice when it resolves having done nothing.
   *
   * ⚠️ `signInWithPasskey` returns `Promise<void>` and swallows `cancelled`
   * silently by design — a red message on every ignored autofill popup would be
   * worse. So the gate detects the NO-OP rather than the reason: the promise
   * settled, no session was established, and no error was raised. That is
   * exactly the ambiguous case, and it is the one that needs an exit.
   *
   * ⚠️ Detected at the CALL SITE rather than by widening `usePasskeyLogin` —
   * that hook mints sessions from a logged-out surface, took 8 adversarial
   * rounds on the server half, and is not something to reshape for one caller.
   */
  const handleUse = React.useCallback(async () => {
    setUseError(null);
    setCeremonyFellThrough(false);
    succeededRef.current = false;
    await signInWithPasskey();
    if (!succeededRef.current) {
      setCeremonyFellThrough(true);
      /**
       * ⚠️ THE AMBIGUOUS FAILURE, NAMED HONESTLY.
       *
       * On web an empty credential store and a user dismissal are THE SAME
       * EVENT — the spec collapses both into `NotAllowedError`. So the reason
       * says what we OBSERVED (the ceremony resolved without a session), not
       * why. `MfaGateFactorChoice`'s escape-hatch comment records that mobile
       * already paid for trying to tell them apart.
       *
       * ⚠️ EMITTED ONLY WHEN `loginError` DID NOT FIRE. A raised error takes the
       * effect above; this is the silent no-op path, which is the one a patient
       * experiences as "I pressed the button and nothing happened".
       */
      if (!loginError) emitFactorAttempt('passkey-use', 'failed', 'cancelled-or-no-credential');
    }
  }, [signInWithPasskey, loginError]);

  /**
   * A passkey was just created on this screen. ⚠️ THE PATIENT IS STILL LOCKED
   * OUT — registration does not mint `aal2`, so the credential exists but the
   * session has not used it. Bring them back to the choice with the list
   * refreshed, so "Use your passkey" is showing and pressable, and flag the
   * transition so the copy acknowledges what they just did rather than
   * presenting an apparently unchanged screen.
   */
  const [justEnrolled, setJustEnrolled] = React.useState(false);
  const handleJustEnrolled = React.useCallback(() => {
    setUseError(null);
    setCeremonyFellThrough(false);
    setJustEnrolled(true);
    setView('choose');
    // ⚠️ AN ADOPTION COST, NOT AN ADOPTION SUCCESS. This patient had to CREATE a
    // credential; `passkey-use` is the one who already had one. Merging the two
    // would make the funnel unable to answer whether passkeys work for people
    // who hold them — the core question behind retiring SMS.
    //
    // ⚠️ NOTE THE GATE IS NOT YET SATISFIED: registration does not mint aal2.
    // The patient still has to USE it, which emits its own `passkey-use` event.
    emitFactorAttempt('passkey-enroll', 'succeeded');
    // ⚠️ THE GATE MUST REFRESH ITS OWN LIST. `MfaGateFactorChoice` and
    // `PasskeyEnrollmentSection` call `usePasskeyEnrollment()` SEPARATELY, so
    // they hold INDEPENDENT state — the section's reload does not update the
    // gate's `credentials`. Without this the gate would return to the choice
    // still believing the list is empty and offer "Set up a passkey" again.
    //
    // ⚠️ NOT awaited, deliberately. The view change must not wait on I/O — that
    // coupling is the defect this whole fix exists to remove. The button is
    // already correct before the refresh lands: `justEnrolled` drives the copy,
    // and `likelyHasPasskey` is optimistic on `null`.
    void refreshCredentials();
  }, [refreshCredentials]);


  const backToChoice = React.useCallback(() => {
    setJustEnrolled(false);
    setUseError(null);
    setView('choose');
    // The list may have changed under an enrollment that partially completed.
    void refreshCredentials();
  }, [refreshCredentials]);

  /**
   * Transient feedback for the verification resend — CU-86akjgbyw.
   *
   * ⚠️ DELIBERATELY SEPARATE FROM THE VERIFICATION STATE. The standing
   * unverified copy carries the instruction the patient still needs; a send
   * must not clear or replace it. The SMS-step equivalent
   * (`SmsEnrollmentSection.tsx:212-216`) makes the same choice for the same
   * reason, and `useEmailVerificationGate.resend` returns a boolean rather than
   * touching state precisely so this stays a call-site concern.
   */
  const [verificationSent, setVerificationSent] = React.useState(false);
  const [verificationFailed, setVerificationFailed] = React.useState(false);

  const handleResendVerification = React.useCallback(async () => {
    // ⚠️ Clear BOTH first: a retry after a failure must not show the old
    // "couldn't send" beside a fresh "sent", which reads as contradicting
    // itself on the one screen a blocked patient depends on.
    setVerificationSent(false);
    setVerificationFailed(false);
    const ok = await emailVerification.resend();
    setVerificationSent(ok);
    setVerificationFailed(!ok);
  }, [emailVerification]);

  /** ⚠️ Rendered in EVERY view — the never-wedged property. */
  const signOut = onSignOut ? (
    <button type="button" onClick={onSignOut} className="text-sm underline" data-testid="mfa-gate-sign-out">
      Sign out
    </button>
  ) : null;

  /**
   * ⚠️ DO NOT SHOW THE FACTOR CHOICE BEFORE THE VERIFICATION READ RESOLVES.
   *
   * Without this the gate renders the full choice for one frame and then
   * replaces it with the unverified screen — and a patient who taps "Set up a
   * passkey" inside that frame lands in the enrolment ceremony the whole task
   * exists to keep them out of. A gate that offers a factor before deciding is
   * not a gate; the same reasoning as `MfaRouteGate`'s `resolving` branch.
   *
   * ⚠️ NOT GATED ON THE CREDENTIAL LIST. That read is allowed to be in flight —
   * `statusSettled` handles it by disabling the button, and blocking the whole
   * screen on it would reproduce the mobile dead end where a failed list left
   * the control disabled forever (CU-86ajx3xg7).
   */
  if (emailVerification.state === 'checking') {
    return (
      <div data-testid="mfa-gate-checking-email" className="flex flex-col gap-4">
        <p className="text-sm">Checking your account…</p>
        {signOut}
      </div>
    );
  }

  /**
   * ⚠️ THE UNVERIFIED PRECONDITION — RENDERED BEFORE EVERY OTHER BRANCH.
   *
   * Placed FIRST on purpose. `view` is not consulted, so a stale
   * `enroll-passkey` / `enroll-sms` value cannot render an enrolment surface
   * underneath an unverified email — which is exactly the state a patient
   * reaches by pressing "Set up a passkey" and then having a recheck resolve
   * `unverified`. A branch ordered after those would leave the trap reachable
   * through the back of the component.
   *
   * ⚠️ ONLY A CONFIRMED `unverified` BLOCKS. `checking` renders its own waiting
   * state below; `unknown` (a reload fault) FALLS THROUGH to the choice,
   * because denying a verified patient both factors over our own network blip
   * is a lockout from their own PHI under live fail-closed enforcement.
   * `blocksEnrollment` encodes this so no call site re-derives it.
   *
   * ⚠️ THE FALL-THROUGH IS A KNOWING TRADE AND IT IS MITIGATED BELOW. There is
   * NO server-side email check on passkey registration (measured: zero
   * `emailVerified` matches across `src/auth/passkey/`), so an unverified
   * patient on the `unknown` path can still enrol a passkey — and a later
   * ADDRESS CHANGE or PASSWORD RESET will reap it (⚠️ NOT verification; see the
   * measurements on the warning block below). We accept that rather than strand
   * a verified patient, and keep the warning visible on that path so nobody
   * loses a credential without being told. See `useEmailVerificationGate`'s
   * `unknown`.
   */
  if (emailVerification.blocksEnrollment) {
    return (
      <div data-testid="mfa-gate-unverified-email" className="flex flex-col gap-4">
        <h2 className="text-xl font-semibold">Verify your email to continue</h2>

        <p className="text-sm">
          To keep your health information secure, your account needs a second way to confirm it&apos;s you — and we can
          only set that up once your email address is verified.
        </p>

        {/* ⚠️ DOES NOT PROMISE AN EMAIL IS WAITING. The verification send
            belongs to CU-86akjgbt1 (server-side, in `createPatient`,
            NON-CONSOLE sources only) and `createPatient` sends nothing today.
            There is no population backfill and none is scoped. So the EXISTING
            unverified cohort — bulk-enrolled, GHL-scheduled, previously
            self-registered — has never been sent one, and that cohort is the
            larger one and precisely who this screen fires for. Telling them to
            "check your email" would send them hunting for a message nobody
            sent. Matches the console's corrected concierge line. */}
        <p className="text-sm">
          There may not be an email waiting for you, so you may need to request one.
        </p>

        {/* ⚠️ THE PASSKEY-DESTRUCTION WARNING — the requirement that keeps this
            task a FIX rather than a relocation of the defect.

            A patient can arrive here ALREADY HOLDING a working pre-verification
            passkey; it signs them in today. Verifying from this screen destroys
            it. Without this warning we would simply have moved the silent
            destruction into the gate.

            ⚠️ FAIL TOWARD WARNING. `likelyHasPasskey` is optimistic on unknown
            (`credentials === null` means the read is in flight or FAILED, never
            "zero"). Warning a patient who turns out to hold none costs a moment
            of confusion; failing to warn one who does costs them their
            credential. ⚠️ NEVER narrow this to a confirmed count, and never
            source it from `mfa.enrollmentState` — that reads "none" for
            passkey-only patients BY DESIGN and already sent one investigation
            down a false trail.

            ⚠️ "MAY NEED TO", NOT "WILL" — SOFTENED 2026-09-16 AFTER A STAGING
            MEASUREMENT FALSIFIED THE CERTAINTY, AND DELIBERATELY NOT REMOVED.

            The barrel's premise was: verification bumps `tokensValidAfterTime`,
            so H4 reaps every passkey enrolled beforehand. ⚠️ THE FIRST HALF IS
            FALSE. Measured three ways on staging:

              1. A full verification round-trip on a real account flipped
                 `emailVerified` false->true and left `tokensValidAfterTime`
                 COMPLETELY UNCHANGED. The passkey kept working.
              2. H4's own comment (`passkeyVerify.ts:310-320`) enumerates the
                 triggers — password reset, password/email UPDATE, and explicit
                 `revokeRefreshTokens`. Verification is not among them; a flag
                 change is not a credential-invalidating event.
              3. Population: 151 of 181 bumped accounts share a TWO-MINUTE
                 window on 2026-08-31 — the cutover sweep's revoke. Verified and
                 unverified accounts were bumped at the same second, so that
                 split is population skew, not causation.

            ⚠️ THE 2026-09-15 REPRODUCTION REMAINS UNEXPLAINED, AND SAYING SO IS
            THE POINT. An earlier draft of this comment blamed the cutover sweep.
            That was an OVERREACH, caught in review: the account measured above
            was created 2026-09-01 — a DAY AFTER the sweep — and its
            `tokensValidAfterTime` equals its creation time exactly (delta 0ms),
            the Firebase default, never bumped by anything. A sweep cannot
            explain a credential it never touched. Generalising from a
            population distribution to a specific account is how the original
            false premise was formed in the first place.

            ⚠️ THE LIKELY REAL TRIGGER IS A PASSWORD CHANGE/RESET, AND IT IS
            NOT A DEFECT. H4 revoking on password change is DELIBERATE — the
            threat model requires it (`CU-86ajnjka0_SECURITY_THREAT_MODEL.md:95`:
            *"Password change/reset REVOKES all enrolled keys… 'I changed my
            password' MUST mean 'I'm safe.'"*), and it is test-pinned at
            `passkeyVerify.test.ts:453`. That fits H4's trigger list, fits the
            2026-09-15 reproduction, and requires no undiscovered mechanism.

            ⚠️ The CLIENT-SIDE half of that — patients never being told, plus a
            measured ~47-MINUTE delay between the password change and the
            resulting lockout — is tracked as **CU-86akjurnv** (Urgent, both
            clients). Do not re-derive it here, and do NOT weaken server-side H4
            to make any of this simpler.

            ⚠️ AN EMAIL *ADDRESS CHANGE* DOES REAP — REPRODUCED END TO END ON
            STAGING, 2026-09-16, VIA THE CONSOLE'S "UPDATE PATIENT EMAIL":

              tokensValidAfter:   12:19:19 (= creation) -> 19:00:08   BUMPED
              passkeyCredentials: 1 -> 0                              DELETED
              emailVerified:      true, KEPT through the change

            The patient could no longer "Sign in with a passkey".

            ⚠️ THIS IS THE DISTINCTION THE WORKSTREAM MISSED, and it is one word
            wide. H4's trigger list says password/email **UPDATE**. VERIFYING an
            email is a flag change and bumps nothing; CHANGING THE ADDRESS is a
            credential-invalidating update and reaps every passkey. The barrel
            read "email" and concluded verification.

            ⚠️ AND IT IS QUIETER THAN THE PASSWORD CASE. `updatePatientEmail.ts:47`
            KEEPS `emailVerified: true` through the change, so the patient still
            reads as verified on every dashboard while their credential is gone —
            no flag moves to mark it. An orgAdmin correcting a typo in an address
            silently destroys that patient's passkey.

            ⚠️ SO WHY KEEP THE WARNING AT ALL? Because the COSTS REMAIN
            ASYMMETRIC and the reap is real — something did destroy that
            patient's credential, and H4 still deletes silently with no audit row
            and no notification. A patient warned unnecessarily loses ten
            seconds; an unwarned one loses a working credential and is never told
            why. "May need to" is the honest shape: it neither promises a
            destruction we cannot demonstrate nor leaves the patient unprepared
            for one we cannot rule out.

            ⚠️ DO NOT restore "will mean" without re-measuring, and DO NOT delete
            this block because the mechanism looks disproven. Both edits have a
            plausible-sounding case and both are wrong.

            ⚠️ BOTH CONSEQUENCES, NOT ONE. The re-enrol half matches the admin
            console's concierge line ("setting up their passkey again
            afterwards", CU-86akjgc6m) — ⚠️ THAT LINE CARRIES THE SAME
            OVERSTATED CERTAINTY and should be softened to match; tracked
            separately rather than edited from here mid-run. The password-manager
            half is carried HERE and deliberately NOT there: the patient performs
            it on their own device after the call ends. Voice and concrete examples follow
            the revocation precedent at
            `mideo-health-app/src/components/PasskeyEnrollmentSection.tsx:161`,
            which warns about the same orphan for a different trigger.

            ⚠️ THE PORTAL'S "NEVER say your password manager" RULE
            (`PasskeyEnrollmentSection.tsx:53-54`) DOES NOT APPLY HERE, and the
            distinction is one careless edit away from being lost. That rule
            governs SYNC-STATUS LABELS derived from `backupEligible` /
            `backupState`, where the flags say a credential IS syncable but
            never WHAT syncs it — so "Synced to your account" is the honest
            phrasing. This is a CLEANUP INSTRUCTION, where naming the likely
            places is the entire point and the app already ships exactly that.
            Do not "align" the two. */}
        {likelyHasPasskey && (
          <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-4" role="note">
            <p className="text-sm font-semibold" data-testid="mfa-gate-passkey-at-risk">
              If you already sign in with a passkey, you may need to set it up again afterwards.
            </p>
            <p className="text-sm">
              If that happens, you may also want to remove the saved MIDEO passkey from your password manager (for
              example iCloud Keychain or 1Password), so you don&apos;t end up with one that no longer works.
            </p>
          </div>
        )}

        {verificationSent && (
          <p className="text-sm text-green-700" data-testid="mfa-gate-verification-sent">
            Sent. Check your inbox — and your spam folder — then come back and select “I&apos;ve verified it”.
          </p>
        )}
        {verificationFailed && (
          <p className="text-sm" role="alert" data-testid="mfa-gate-verification-failed">
            We couldn&apos;t send it just now. Please try again shortly, or contact{' '}
            <a href="mailto:info@mideohealth.com">info@mideohealth.com</a>.
          </p>
        )}

        <div className="flex flex-col gap-2">
          {/* ⚠️ RECHECK IS AN EXPLICIT BUTTON PRESS, NEVER A RENDER-DRIVEN
              POLL. `reload()` is a network call; the prior art on this very
              component is 20,379 token-refresh requests from one enrolment. */}
          <Button
            id="mfa-gate-recheck-verification"
            type="button"
            onClick={() => void emailVerification.recheck()}
            textContent="I've verified it — check again"
            disabled={emailVerification.busy}
            working={emailVerification.busy}
            workingText="Checking…"
          />
          {/* ⚠️ THE ONLY WAY A PATIENT CAN UNBLOCK THEMSELVES. The Firebase
              console has NO admin-side "send verification email" action, so
              support cannot do it for them. Without this, a patient whose
              original email never arrived has no route forward at all — and
              under live fail-closed enforcement that is a lockout from their
              own PHI. */}
          <button
            type="button"
            onClick={() => void handleResendVerification()}
            className="self-start text-sm underline"
            disabled={emailVerification.busy}
            data-testid="mfa-gate-resend-verification"
          >
            Send me a verification email
          </button>
        </div>

        <p className="text-sm">
          Need help? Contact <a href="mailto:info@mideohealth.com">info@mideohealth.com</a> and we&apos;ll help you find
          another way in.
        </p>

        {/* ⚠️ NEVER WEDGED. A patient must always be able to leave a screen they
            cannot satisfy — and this screen is satisfiable only via an inbox
            they may not be able to reach. */}
        {signOut}
      </div>
    );
  }

  if (view === 'enroll-passkey') {
    return (
      <div data-testid="mfa-gate-enroll-passkey" className="flex flex-col gap-4">
        {/* ⚠️ `onEnrolled` DOES NOT CALL `onComplete` — measured on staging
            2026-08-13, and this is the CU-86ajw3hv9 property biting from the
            other side.

            `passkeyRegister` does NOT mint `aal2`; only `passkeyVerify` does.
            So a patient who enrols HERE is still AAL1, and re-deciding the gate
            would return `enroll` again — dropping them back on the very screen
            they just completed, with no indication of what went wrong. On
            staging this presented as a green "Passkey added." panel that led
            nowhere; only a manual page refresh revealed the "Use your passkey"
            button, because the mount read then found the new credential.

            ⚠️ SO ENROLLMENT HANDS OFF TO THE CEREMONY, not to the decision.
            `handleJustEnrolled` refreshes the list and returns to the choice
            with the use-path primed, which is the actual way through the gate. */}
        {/* ⚠️ `onEnrolledImmediate`, NOT `onEnrolled` — staging 2026-08-13, the
            SECOND occurrence. `onEnrolled` fires only after the section awaits
            its own list reload, so the green "Passkey added." panel renders
            while the handoff is still waiting on a network round trip, and
            never fires at all if that reload fails. The patient sat looking at
            a success box with no way forward. `onEnrolledImmediate` fires on
            the same `done` transition that renders the panel, so the two
            cannot disagree. */}
        {/* ── ⚠️ WHY ENROLLING A PASSKEY *AT THE GATE* IS ACCEPTED ──────────
            Reviewed 2026-09-23. The question recurs, so the answer is recorded
            here rather than re-derived.

            ⚠️ THE STEP-UP IS PASSWORD-REAUTH ONLY. `usePasskeyEnrollment`
            requires an explicit reauthentication (its own comment calls this
            NON-NEGOTIABLE), which defeats a SESSION HIJACKER — someone holding a
            stolen token but no password.

            ⚠️ IT DOES NOT DEFEAT A STOLEN PASSWORD. That attacker reauths
            successfully, enrols their own passkey and holds durable AAL2 from
            their own device. For a PASSKEY-ONLY patient this means the gate is
            effectively password -> AAL2: the second factor can be minted by
            whoever holds the first.

            ⚠️ IT IS ACCEPTED ANYWAY, for two reasons that must BOTH hold:

            1. REMOVING IT WOULD STRAND PATIENTS. The gate's escape hatch for a
               patient with no usable phone IS passkey enrollment. Closing it
               leaves SMS as the only route — the exact strand
               NO-STRAND-ANYFACTOR exists to prevent.

            2. THE COMPENSATING CONTROL IS DETECTIVE AND ACTIONABLE. Enrollment
               sends a notification naming the action and the device, with
               remediation in the CORRECT ORDER — revoke-all first (which kills
               the planted credential AND the attacker's sessions), then change
               the password. Verified by receiving one, 2026-09-23.

            ⚠️ IF THE NOTIFICATION EVER STOPS FIRING, OR LOSES THE REVOKE-ALL
            INSTRUCTION, THIS TRADE-OFF NO LONGER HOLDS. It is the only thing
            standing between a stolen password and durable AAL2 here.

            ⚠️ Enrolling from the PROFILE page carries the same property, but the
            patient there already holds AAL2 — so it is not a step-up at all. */}
        <PasskeyEnrollmentSection
          variant="gate"
          onEnrolledImmediate={handleJustEnrolled}
          onCancel={backToChoice}
        />
        {signOut}
      </div>
    );
  }

  if (view === 'enroll-sms') {
    return (
      <div data-testid="mfa-gate-enroll-sms" className="flex flex-col gap-4">
        {/* ⚠️ `onCancel` RETURNS TO THE CHOICE, never out of the gate. This is
            the omission the gate's placeholder comment described: previously
            there was nowhere to cancel TO, so it was deliberately not passed. */}
        {/* ⚠️ THE SMS ARM OF THE FUNNEL. Emitted on COMPLETION, not on entering
            the view — a patient who opens the SMS section and abandons it has
            not chosen SMS, and counting them as such would overstate exactly the
            tier we are trying to decide whether we can retire. */}
        <SmsEnrollmentSection
          variant="gate"
          onComplete={handleSmsComplete}
          onCancel={backToChoice}
        />
        {signOut}
      </div>
    );
  }

  return (
    <div data-testid="mfa-gate-choice" className="flex flex-col gap-4">
      <h2 className="text-xl font-semibold">Confirm it&apos;s you to continue</h2>

      {/* ⚠️ ACKNOWLEDGE THE STEP THEY JUST TOOK. Without this, a patient who has
          just enrolled is returned to a screen that looks identical to the one
          they started on, and reasonably concludes the enrollment failed. The
          passkey exists — it simply has not been USED yet, and using it is what
          opens the gate. */}
      {justEnrolled ? (
        <p className="text-sm" data-testid="mfa-gate-just-enrolled">
          Your passkey is saved. One last step — confirm it below to continue to your health information.
        </p>
      ) : (
        <p className="text-sm">
          To keep your health information secure, your account needs a second way to confirm it&apos;s you.
        </p>
      )}

      {useError && (
        <p role="alert" className="text-sm text-red-700">
          {useError}
        </p>
      )}

      {/* ⚠️ THE FALL-THROUGH MITIGATION — adversarial round, F1 (2026-09-16).
          Shown when we could NOT read the verification state (`unknown`, a
          reload fault) and the patient may hold a passkey.

          ⚠️ WHY IT IS HERE AT ALL. There is NO server-side email check on
          passkey registration — measured: `grep -rn "emailVerified"
          src/auth/passkey/` returns zero across 25 files. IdP's rule covers
          SMS only. So this path can enrol a passkey that a later verification
          silently reaps, which is the very order the gate exists to prevent.
          We accept the fall-through rather than strand a verified patient on a
          network blip, but NOT silently: the patient who might lose a
          credential is told first.

          ⚠️ NOT SHOWN ON `verified` — verification already happened, so there
          is nothing left to destroy and this would be pure noise on the common
          path. Deliberately narrower than the blocked screen's warning. */}
      {emailVerification.state === 'unknown' && likelyHasPasskey && (
        <div className="flex flex-col gap-2 rounded-lg border border-amber-300 bg-amber-50 p-4" role="note">
          {/* ⚠️ NAMES THE REAL TRIGGERS — CORRECTED 2026-09-16 after staging.
              This said "if you verify it later, any passkey you set up now will
              stop working." ⚠️ THAT MECHANISM IS FALSE: verification does not
              bump `tokensValidAfterTime` and reaps nothing (measured). What does
              is an email ADDRESS CHANGE (reproduced: 1 credential -> 0) or a
              PASSWORD RESET (CU-86akjurnv, by design).

              ⚠️ It also promised the wrong thing to the wrong person. Offline,
              `credentials === null` means the read FAILED, so this shows to
              patients with no passkey at all — confirmed on a brand-new account
              during Run 5. Now phrased so it is true either way: conditional on
              having one, and naming causes rather than a false certainty. */}
          <p className="text-sm" data-testid="mfa-gate-unknown-passkey-at-risk">
            We couldn&apos;t check your account details just now. If you set up a passkey, remember that changing your
            email address or resetting your password will mean setting it up again afterwards.
          </p>
        </div>
      )}

      {/* ── Tier 0 — passkey. FIRST, and visually primary. ─────────────────
          It needs no phone number, which is why it leads: a patient without a
          usable phone is not stranded. */}
      {supported && (
        <div className="flex flex-col gap-2 rounded-lg border p-4">
          {likelyHasPasskey ? (
            <>
              {/* ⚠️ "USE", NOT "SET UP" — the CU-86ajw3hv9 correction. This
                  patient probably already holds a working credential; a password
                  login simply does not mint aal2. */}
              <h3 className="text-lg font-semibold">
                {justEnrolled ? 'Continue with your new passkey' : 'Use your passkey'}
              </h3>
              <p className="text-sm">
                Confirm with your fingerprint, face or device PIN. It&apos;s the fastest way back in, and it doesn&apos;t
                need a phone signal.
              </p>
              <Button
                id="mfa-gate-use-passkey"
                type="button"
                onClick={() => void handleUse()}
                // ⚠️ The label follows the patient's own last action. Straight
                // after enrolling, "Use your passkey" reads as a generic offer
                // and invites the question "which passkey?" — the one they just
                // made is the answer, so the button says so.
                textContent={justEnrolled ? 'Continue with your new passkey' : 'Use your passkey'}
                // ⚠️ NOT PRESSABLE UNTIL THE READ SETTLES. `busy` does not cover
                // this — the hook sets it for enroll/revoke, never for the status
                // fetch. Gated on SETTLED, not on the value, so a FAILED read
                // still releases the button (mobile's permanent dead end).
                disabled={!statusSettled || loginBusy}
                working={loginBusy || !statusSettled}
                workingText={statusSettled ? 'Waiting for your device…' : 'Checking…'}
              />
              {/* ⚠️ THE OFFERED EXIT (F2). Shown once a use-attempt resolved
                  without establishing a session — which on web means EITHER the
                  patient dismissed the sheet OR their store held nothing for
                  this domain. Those are the same event here, so we name the
                  ambiguity honestly and let the patient choose, rather than
                  guessing and auto-navigating someone who simply changed their
                  mind. */}
              {ceremonyFellThrough && (
                <p className="text-sm" data-testid="mfa-gate-passkey-fallback">
                  If your device didn&apos;t offer a passkey, you may not have one saved for this site yet — you can{' '}
                  <button
                    type="button"
                    onClick={() => setView('enroll-passkey')}
                    className="underline"
                    data-testid="mfa-gate-fallback-enroll"
                  >
                    set one up now
                  </button>
                  .
                </p>
              )}
              <button
                type="button"
                onClick={() => setView('enroll-passkey')}
                className="self-start text-sm underline"
                data-testid="mfa-gate-add-passkey-instead"
              >
                Add a different passkey
              </button>
            </>
          ) : (
            <>
              <h3 className="text-lg font-semibold">Set up a passkey</h3>
              <p className="text-sm">
                Sign in with your fingerprint, face or device PIN instead of a texted code. It doesn&apos;t need a phone
                signal.
              </p>
              <Button
                id="mfa-gate-setup-passkey"
                type="button"
                onClick={() => setView('enroll-passkey')}
                textContent="Set up a passkey"
                disabled={!statusSettled}
                working={!statusSettled}
                workingText="Checking…"
              />
            </>
          )}
        </div>
      )}

      {/* ── Tier 1 — SMS. ⚠️ ALWAYS OFFERED AND NEVER GATED behind passkey
          support: a patient on a browser without WebAuthn must still get
          through. This block sits OUTSIDE the `supported` check deliberately. */}
      <div className="flex flex-col gap-2 rounded-lg border p-4">
        <h3 className="text-lg font-semibold">Use a texted code</h3>
        <p className="text-sm">
          We&apos;ll text a code to your mobile number each time you sign in.
          {!supported && ' This browser can’t use passkeys, so a texted code is the way in here.'}
        </p>
        <Button
          id="mfa-gate-setup-sms"
          type="button"
          onClick={() => setView('enroll-sms')}
          textContent="Set up a texted code"
        />
      </div>

      {/* ⚠️ TIER 2 IS NEVER OFFERED. The grace extension is admin-granted and
          expiring, so showing it as a choice would offer a control the patient
          cannot action. A patient who can do neither tier is routed to a human. */}
      <p className="text-sm">
        Can&apos;t do either of these? Contact <a href="mailto:info@mideohealth.com">info@mideohealth.com</a> and
        we&apos;ll help you find another way in.
      </p>

      {signOut}
    </div>
  );
}

export default MfaGateFactorChoice;
